// 라이브 조서 — officejs 가 엑셀 시트에 단계별로 조서를 써 내려가듯, 웹에서는
// 이 박스에 텍스트 행이 계속 쌓인다 (진행 로그 다음, 최종 결과 박스 전).
// 데이터 원천은 폴링 partial(유사기업·산업분류)과 step(버핏) — 도착한 것만 쓴다.
import { useEffect, useRef, useState } from "react";
import { useLabState } from "../../lib/labState";

export interface SheetLine {
  // prog: 진행 중에만 보여주는 중간 행 — 완료 시 clearProg() 로 걷어낸다
  kind: "title" | "head" | "row" | "note" | "prog";
  cells: string[];
  // 다열 표의 열 폭 — CSS grid-template-columns 문자열. 기본 flex 레이아웃은
  // 라벨:값 2열용이라 3열 이상 표는 행마다 폭이 달라 어긋난다(유사기업 조서
  // 실측). head 와 row 에 같은 grid 를 주면 엑셀처럼 열이 맞는다.
  grid?: string;
  // 펼침 상세 — 있으면 행을 눌러 펼친다(PDF 조서의 '유사성 근거'·'제외 사유' 문구).
  detail?: string[];
}

export function useLiveSheet(persistKey?: string) {
  // persistKey 를 주면 페이지를 나갔다 와도 조서가 유지된다 (labState 참조).
  const local = useState<SheetLine[]>([]);
  const persisted = useLabState<SheetLine[]>(persistKey || "sheet.unused", []);
  const [lines, setLines] = persistKey ? persisted : local;
  const append = (add: SheetLine[]) => {
    if (!add.length) return;
    setLines((prev) => [...prev, ...add].slice(-600));
  };
  const reset = () => setLines([]);
  const clearProg = () => setLines((prev) => prev.filter((l) => l.kind !== "prog"));
  return { lines, append, reset, clearProg };
}

export default function LiveSheet({ lines, running }: { lines: SheetLine[]; running: boolean }) {
  const boxRef = useRef<HTMLDivElement>(null);
  // 새 행이 붙을 때마다 맨 아래로 — 조서가 '써지고 있다'는 감각의 핵심
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    if (el.scrollHeight > el.clientHeight) {
      el.scrollTop = el.scrollHeight;
    } else {
      // 내부 스크롤이 없어진 뒤로는 페이지 스크롤러(.lab-scroll)가 따라가야 한다
      // — nearest 라 화면 밖일 때만 살짝 당겨온다
      el.lastElementChild?.scrollIntoView({ block: "nearest" });
    }
  }, [lines.length]);

  if (lines.length === 0) return null;
  return (
    <section className="lab-card">
      <div className="lab-progress-head" style={{ marginBottom: 8 }}>
        검토 조서{running ? " — 작성 중…" : " (작성 완료)"}
        {running && <span className="lab-timer">단계가 끝날 때마다 이어서 기록됩니다</span>}
      </div>
      <div className="live-sheet" ref={boxRef}>
        {lines.map((l, i) => {
          const cls = `ls-line ls-${l.kind}${l.grid ? " ls-grid" : ""}`;
          const style = l.grid ? { gridTemplateColumns: l.grid } : undefined;
          if (!l.detail?.length) {
            return (
              <div key={i} className={cls} style={style}>
                {l.cells.map((c, j) => <span key={j} className="ls-cell">{c}</span>)}
              </div>
            );
          }
          // 접힌 행 — 표(grid)는 회사명 칸 앞(열 간격 안으로 내어 써서 머리글과 정렬), 2열 행은 첫 칸 앞에 펼침 표시
          const caretAt = l.grid ? 1 : 0;
          return (
            <details key={i} className="ls-fold">
              <summary className={cls} style={style}>
                {l.cells.map((c, j) => (
                  <span key={j} className="ls-cell">{j === caretAt && <span className="ls-caret" aria-hidden />}{c}</span>
                ))}
              </summary>
              <div className="ls-fold-body">
                {l.detail.map((d, j) => <div key={j}>{d}</div>)}
              </div>
            </details>
          );
        })}
        {running && <div className="ls-line ls-cursor"><span className="ls-cell">▌</span></div>}
      </div>
    </section>
  );
}

// ── 유사기업(사업내용 AI) partial → 조서 행 변환 ──────────────────────
// officejs PeerExcelWriter 의 섹션 구성(키워드 → 2단계 본문 → 2.5 매출구조 →
// 3단계 정성 제외 → 최종)을 텍스트 행으로 재현한다.
type PartialSection = "keywords" | "step2" | "step25" | "step3";
const SECTION_ORDER: PartialSection[] = ["keywords", "step2", "step25", "step3"];
const SECTION_TITLE: Record<PartialSection, string> = {
  keywords: "[1단계] 주요 사업 키워드 도출",
  step2: "[2단계] 사업보고서 본문 검토 — 유사 사업 후보",
  step25: "[3단계] 매출구조 정밀검토",
  step3: "[4단계] 정성적 제외 기준 적용",
};

function entryLine(e: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const k of ["detail", "summary", "note", "reason"]) {
    if (typeof e[k] === "string" && e[k]) parts.push(String(e[k]));
  }
  for (const k of ["count", "passed", "passed_count", "excluded", "matched"]) {
    if (typeof e[k] === "number") parts.push(`${k}=${e[k]}`);
  }
  return parts.join(" · ") || JSON.stringify(e).slice(0, 140);
}

export function peerPartialLines(
  partial: unknown,
  markets: string[],
  written: Set<string>,
): SheetLine[] {
  const out: SheetLine[] = [];
  if (!partial || typeof partial !== "object") return out;
  const p = partial as Record<string, { keywords?: { primary?: string[]; secondary?: string[] }; filter_log?: Record<string, unknown>[] }>;
  for (const mkt of markets) {
    const mp = p[mkt];
    if (!mp) continue;
    const label = markets.length > 1 ? (mkt === "us" ? " (미국)" : " (국내)") : "";
    for (const sec of SECTION_ORDER) {
      const key = `${mkt}:${sec}`;
      if (written.has(key)) continue;
      if (sec === "keywords") {
        if (!mp.keywords) continue;
        written.add(key);
        out.push({ kind: "title", cells: [SECTION_TITLE.keywords + label] });
        if (mp.keywords.primary?.length) out.push({ kind: "row", cells: ["주요 키워드", mp.keywords.primary.join(", ")] });
        if (mp.keywords.secondary?.length) out.push({ kind: "row", cells: ["보조 키워드", mp.keywords.secondary.join(", ")] });
        continue;
      }
      const prefix = sec === "step2" ? "2_" : sec === "step25" ? "2.5_" : "3_";
      const entries = (mp.filter_log || []).filter(
        (e) => typeof e.step === "string" && (e.step as string).startsWith(prefix));
      if (!entries.length) continue;
      written.add(key);
      out.push({ kind: "title", cells: [SECTION_TITLE[sec] + label] });
      for (const e of entries.slice(0, 12)) {
        out.push({ kind: "row", cells: [String(e.step || ""), entryLine(e)] });
      }
    }
  }
  return out;
}

type PeerRemarks = {
  warnings?: string[]; 선정근거?: string; 사업개요_매칭키워드?: string[]; 매출구조_분석?: string; 관련매출비중?: string;
};
type PeerFinal = {
  name?: string; stock_code?: string; ticker?: string; composite?: { total?: number };
  score?: number; remarks?: PeerRemarks; matched_keywords?: string[]; revenue_comment?: string; warnings?: string[];
};
type FilterEntry = {
  step?: string;
  excluded?: { name?: string; stock_code?: string; ticker?: string; reason?: string; comment?: string; 매출구조_분석?: string }[];
  not_selected?: { name?: string; stock_code?: string; ticker?: string; score?: number; keyword_score?: number | string }[];
};

/** PDF 조서 '⑤ 회사별 유사성 근거'와 같은 문구 — 매칭 키워드 · 매출구조 · 관련 매출 비중 · [주의]
 *  (선정근거는 키워드+매출구조를 이어 붙인 문장이라 중복이므로 싣지 않는다) */
function peerBasis(p: PeerFinal): string[] {
  const rem = p.remarks || {};
  const out: string[] = [];
  const kws = rem.사업개요_매칭키워드 || p.matched_keywords || [];
  if (kws.length) out.push(`매칭 키워드: ${kws.join(", ")}`);
  const rv = rem.매출구조_분석 || p.revenue_comment || "";
  if (rv) out.push(`매출구조: ${rv}`);
  if (rem.관련매출비중 && rem.관련매출비중 !== "미상") out.push(`관련 매출 비중: ${rem.관련매출비중}`);
  for (const w of rem.warnings || p.warnings || []) out.push(`[주의] ${w}`);
  return out;
}

const codeOf = (e: { ticker?: string; stock_code?: string }) => e.ticker || e.stock_code || "";

/** PDF 조서 '⑥ 제외 내역'과 같은 문구 — 단계별로 묶어 단계 행을 펼치면 회사별 사유 */
function exclusionLines(filterLog: FilterEntry[]): SheetLine[] {
  const rows: SheetLine[] = [];
  let total = 0;
  for (const fl of filterLog) {
    const detail: string[] = [];
    for (const e of fl.excluded || []) {
      // 4b 거래정지 단계는 사유 필드 없이 회사만 담긴다
      const why = e.reason || e.comment || e.매출구조_분석 || (String(fl.step || "").includes("거래정지") ? "거래정지" : "");
      detail.push(`${e.name || ""}${codeOf(e) ? ` (${codeOf(e)})` : ""} — ${why || "사유 미기재"}`);
    }
    for (const e of fl.not_selected || []) {
      const sc = typeof e.score === "number" ? `종합점수 ${e.score.toFixed(3)}` : "종합점수 미달";
      detail.push(`${e.name || ""}${codeOf(e) ? ` (${codeOf(e)})` : ""} — Top-K 순위 밖 (${sc}, 키워드 ${e.keyword_score ?? "—"})`);
    }
    if (!detail.length) continue;
    total += detail.length;
    rows.push({ kind: "row", cells: [String(fl.step || "").replace(/_/g, " "), `${detail.length}개사 제외`], detail });
  }
  if (!rows.length) return [];
  return [{ kind: "title", cells: [`[제외 내역] ${total}개사 — 단계를 누르면 제외 사유가 펼쳐집니다`] }, ...rows];
}

/** 최종 결과 → 조서 마무리 행 (선정 기업 나열 + 펼침 유사성 근거, 제외 내역) */
export function peerFinalLines(result: {
  peers?: PeerFinal[];
  peer_count?: number;
  filter_log?: FilterEntry[];
}): SheetLine[] {
  const peers = result.peers || [];
  // 엑셀 조서와 같은 열 구성 — 비고에는 경고(거래정지 이력·낮은 매출 관련도)를,
  // 없으면 선정근거 요약을 싣는다. 행을 누르면 PDF 의 유사성 근거 전문이 펼쳐진다.
  const GRID = "34px minmax(0,1.1fr) 72px 56px minmax(0,2fr)";
  const out: SheetLine[] = [
    { kind: "title", cells: [`[최종] 유사 기업 선정 — ${result.peer_count ?? peers.length}개사 · 회사를 누르면 유사성 근거가 펼쳐집니다`] },
    { kind: "head", cells: ["#", "회사명", "코드", "점수", "비고"], grid: GRID },
  ];
  peers.forEach((pr, i) => {
    const sc = pr.composite?.total ?? pr.score;
    const warns = pr.remarks?.warnings || [];
    const remark = warns.length ? warns.join(" · ")
      : String(pr.remarks?.선정근거 || "").slice(0, 120);
    const basis = peerBasis(pr);
    out.push({
      kind: "row", grid: GRID,
      cells: [String(i + 1), String(pr.name || ""), String(pr.ticker || pr.stock_code || ""),
              sc != null ? sc.toFixed(3) : "–", remark],
      ...(basis.length ? { detail: basis } : {}),
    });
  });
  return [...out, ...exclusionLines(result.filter_log || [])];
}

// ── 산업분류 partial(stage1~4) → 조서 행 ─────────────────────────────
const STAGE_TITLE: Record<string, string> = {
  stage1: "1차 · 산업분류 모집단",
  stage2: "2차 · 재무 요건 판정",
  stage3: "3차 · 사업 유사성 (매출구조)",
  stage4: "4차 · 일반 요건",
  final: "[최종] 유사기업 선정",
};

export function industryStageLines(
  partial: Record<string, unknown>,
  written: Set<string>,
): SheetLine[] {
  const out: SheetLine[] = [];
  for (const key of ["stage1", "stage2", "stage3", "stage4", "final"]) {
    const st = partial[key] as { count?: number; passed_count?: number; companies?: Record<string, unknown>[] } | undefined;
    if (!st || written.has(key)) continue;
    written.add(key);
    const n = st.passed_count ?? st.count ?? st.companies?.length;
    out.push({ kind: "title", cells: [`${STAGE_TITLE[key]}${typeof n === "number" ? ` — ${n}개사` : ""}`] });
    for (const c of (st.companies || []).slice(0, 15)) {
      out.push({
        kind: "row",
        cells: [String(c.stock_code || c.code || ""), String(c.name || c.company_name || ""),
                String(c.verdict || c.industry || c.market || "")].filter(Boolean),
      });
    }
    if ((st.companies || []).length > 15) {
      out.push({ kind: "note", cells: [`… 외 ${(st.companies || []).length - 15}개사 (아래 결과 박스에서 전체 확인)`] });
    }
  }
  return out;
}

// ── 버핏 — step 스트림 + 완료 시 해자 채점 행 ────────────────────────
export function buffettFinalLines(
  moats: Record<string, { score?: number; evidence?: string }>,
  labels: Record<string, string>,
  score: number | null,
  rating: string,
): SheetLine[] {
  const out: SheetLine[] = [
    { kind: "title", cells: ["[채점] 경제적 해자 6대 축"] },
    { kind: "head", cells: ["항목", "점수", "근거"] },
  ];
  for (const [k, m] of Object.entries(moats)) {
    out.push({ kind: "row", cells: [labels[k] || k, `${m.score ?? "–"}/10`, (m.evidence || "").slice(0, 80)] });
  }
  if (score != null) {
    out.push({ kind: "title", cells: [`[종합] ${score}/100 — ${rating}`] });
  }
  return out;
}
